// useSynastry - Hook for managing synastry (relationship compatibility) state and API calls
import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, collection, getDocs, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { functions, db, appId } from '../utils/firebase';

export const hasFiniteCoordinate = (value) =>
    typeof value === 'number' && Number.isFinite(value);

export const hasValidCoordinates = (person) =>
    hasFiniteCoordinate(person?.latitude) && hasFiniteCoordinate(person?.longitude);

export const isPersonReady = (person) =>
    Boolean(person?.birthDate && (person.unknownTime || person.birthTime) && hasValidCoordinates(person));

export const useSynastry = (currentUser = null, tonePrefix = '') => {
    // Person A state (typically the logged-in user)
    const [personA, setPersonA] = useState({
        name: '',
        birthDate: '',
        birthTime: '',
        location: '',
        latitude: null,
        longitude: null,
        timezone: '',
        unknownTime: false
    });

    // Person B state (partner/other person)
    const [personB, setPersonB] = useState({
        name: '',
        birthDate: '',
        birthTime: '',
        location: '',
        latitude: null,
        longitude: null,
        timezone: '',
        unknownTime: false
    });

    // Location search state
    const [locationResults, setLocationResults] = useState([]);
    const [isSearchingLocation, setIsSearchingLocation] = useState(false);
    const [activePersonForLocation, setActivePersonForLocation] = useState(null); // 'A' or 'B'

    // Synastry result state
    const [synastryData, setSynastryData] = useState(null);
    const [interpretation, setInterpretation] = useState('');

    // UI state
    const [currentStep, setCurrentStep] = useState(0); // 0: PersonA, 1: PersonB, 2: Confirm, 3: Results
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingInterpretation, setIsLoadingInterpretation] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [error, setError] = useState('');

    // Saved readings state
    const [savedReadings, setSavedReadings] = useState([]);
    const [isLoadingSavedReadings, setIsLoadingSavedReadings] = useState(false);

    /**
     * Search for locations using geocoding
     */
    const searchLocation = useCallback(async (query, forPerson = 'B') => {
        if (!query || query.length < 2) {
            setLocationResults([]);
            setActivePersonForLocation(null);
            return;
        }

        setIsSearchingLocation(true);
        setActivePersonForLocation(forPerson);
        setError('');

        try {
            const geocodeFunction = httpsCallable(functions, 'geocodeLocation');
            const result = await geocodeFunction({ query });
            setLocationResults(result.data || []);
        } catch (err) {
            console.error('Geocoding error:', err);
            setError('Failed to search location. Please try again.');
            setLocationResults([]);
        } finally {
            setIsSearchingLocation(false);
        }
    }, []);

    /**
     * Select a location from search results
     */
    const selectLocation = useCallback(async (location, forPerson = 'B') => {
        const updatePerson = forPerson === 'A' ? setPersonA : setPersonB;

        updatePerson(prev => ({
            ...prev,
            location: location.displayName,
            latitude: location.latitude,
            longitude: location.longitude,
            timezone: location.timezone || prev.timezone
        }));
        setLocationResults([]);
        setActivePersonForLocation(null);

        // Get timezone for the location
        if (location.timezone) {
            return;
        }

        try {
            const getTimezoneFunction = httpsCallable(functions, 'getTimezone');
            const result = await getTimezoneFunction({
                latitude: location.latitude,
                longitude: location.longitude
            });
            updatePerson(prev => ({
                ...prev,
                timezone: result.data.timezone
            }));
        } catch (err) {
            console.error('Timezone lookup error:', err);
            const offset = Math.round(location.longitude / 15);
            updatePerson(prev => ({
                ...prev,
                timezone: `Etc/GMT${offset >= 0 ? '-' : '+'}${Math.abs(offset)}`
            }));
        }
    }, []);

    /**
     * Load Person A from a saved birth chart
     */
    const loadPersonAFromChart = useCallback((chart) => {
        if (!chart) return;

        setPersonA({
            name: chart.name || 'Me',
            birthDate: chart.birthDate || '',
            birthTime: chart.birthTime || '',
            location: chart.birthLocation || '',
            latitude: chart.coordinates?.latitude ?? null,
            longitude: chart.coordinates?.longitude ?? null,
            timezone: chart.timezone || '',
            unknownTime: chart.unknownTime || false
        });
    }, []);

    /**
     * Calculate synastry
     */
    const calculateSynastry = useCallback(async () => {
        // Validate both persons have required data
        if (!isPersonReady(personA) || !isPersonReady(personB)) {
            setError('Please complete birth data for both people.');
            return null;
        }

        setIsLoading(true);
        setError('');
        setSynastryData(null);

        try {
            const calculateFunction = httpsCallable(functions, 'calculateSynastry');
            const result = await calculateFunction({
                personA: {
                    name: personA.name || 'You',
                    birthDate: personA.birthDate,
                    birthTime: personA.unknownTime ? '12:00' : personA.birthTime,
                    latitude: personA.latitude,
                    longitude: personA.longitude,
                    timezone: personA.timezone || 'UTC',
                    location: personA.location,
                    unknownTime: personA.unknownTime
                },
                personB: {
                    name: personB.name || 'Partner',
                    birthDate: personB.birthDate,
                    birthTime: personB.unknownTime ? '12:00' : personB.birthTime,
                    latitude: personB.latitude,
                    longitude: personB.longitude,
                    timezone: personB.timezone || 'UTC',
                    location: personB.location,
                    unknownTime: personB.unknownTime
                }
            });

            setSynastryData(result.data);
            return result.data;
        } catch (err) {
            console.error('Synastry calculation error:', err);
            setError('Failed to calculate compatibility. Please try again.');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [personA, personB]);

    /**
     * Get AI interpretation for the synastry
     */
    const getInterpretation = useCallback(async (data = null) => {
        const synastry = data || synastryData;
        if (!synastry) {
            setError('No synastry data available for interpretation.');
            return;
        }

        setIsLoadingInterpretation(true);
        setError('');

        try {
            const getTarotReadingFunction = httpsCallable(functions, 'getTarotReading');

            // Build a comprehensive prompt
            const topAspects = synastry.topAspects.slice(0, 5)
                .map(a => `${a.planetA} ${a.aspectSymbol} ${a.planetB} (${a.theme})`).join(', ');

            const toneInstructions = tonePrefix || 'You are a mystical relationship astrologer.';
            const prompt = `${toneInstructions} Provide a comprehensive compatibility reading for two people:

Person A (${synastry.personA.name}): ${synastry.bigThree.personA.sun} Sun, ${synastry.bigThree.personA.moon} Moon, ${synastry.bigThree.personA.rising} Rising

Person B (${synastry.personB.name}): ${synastry.bigThree.personB.sun} Sun, ${synastry.bigThree.personB.moon} Moon, ${synastry.bigThree.personB.rising} Rising

Compatibility Score: ${synastry.compatibility.score}%
Harmonious Aspects: ${synastry.compatibility.harmonious}
Challenging Aspects: ${synastry.compatibility.challenging}

Key Aspects: ${topAspects}

Provide an insightful interpretation covering:
1. Overall compatibility and soul connection
2. Emotional and communication dynamics
3. Passion and attraction
4. Growth opportunities and challenges
5. A cosmic message for their relationship

About 4-5 paragraphs.`;

            const result = await getTarotReadingFunction({ promptText: prompt });
            const text = result.data?.candidates?.[0]?.content?.parts?.[0]?.text || '';
            setInterpretation(text);
            return text;
        } catch (err) {
            console.error('Interpretation error:', err);
            setError('Failed to generate interpretation. Please try again.');
            return null;
        } finally {
            setIsLoadingInterpretation(false);
        }
    }, [synastryData, tonePrefix]);

    /**
     * Save synastry reading to Firestore
     */
    const saveSynastry = useCallback(async (name = 'Compatibility Reading') => {
        if (!currentUser || currentUser.isAnonymous) {
            setError('Please sign in to save your reading.');
            return null;
        }

        if (!synastryData) {
            setError('No synastry data to save.');
            return null;
        }

        setIsSaving(true);
        setError('');

        try {
            const readingId = `synastry_${Date.now()}`;
            const readingsPath = `artifacts/${appId}/users/${currentUser.uid}/synastryReadings/${readingId}`;
            console.log('Saving synastry to path:', readingsPath);

            await setDoc(doc(db, readingsPath), {
                name,
                personA,
                personB,
                synastryData,
                interpretation,
                createdAt: serverTimestamp(),
                userId: currentUser.uid
            });

            setIsSaved(true);
            return readingId;
        } catch (err) {
            console.error('Save synastry error:', err);
            setError('Failed to save reading. Please try again.');
            return null;
        } finally {
            setIsSaving(false);
        }
    }, [currentUser, synastryData, interpretation, personA, personB]);

    /**
     * Load saved readings from Firestore
     */
    const loadSavedReadings = useCallback(async () => {
        if (!currentUser || currentUser.isAnonymous) {
            return [];
        }

        setIsLoadingSavedReadings(true);
        try {
            const readingsPath = `artifacts/${appId}/users/${currentUser.uid}/synastryReadings`;
            console.log('Loading synastry from path:', readingsPath);
            const q = query(collection(db, readingsPath), orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);
            console.log('Found synastry readings:', snapshot.docs.length);

            const readings = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setSavedReadings(readings);
            return readings;
        } catch (err) {
            console.error('Load saved readings error:', err);
            return [];
        } finally {
            setIsLoadingSavedReadings(false);
        }
    }, [currentUser]);

    /**
     * Load a specific saved reading into the current state
     */
    const loadSavedReading = useCallback((reading) => {
        if (!reading) return;

        // Restore person data
        if (reading.personA) setPersonA(reading.personA);
        if (reading.personB) setPersonB(reading.personB);

        // Restore synastry data
        if (reading.synastryData) setSynastryData(reading.synastryData);
        if (reading.interpretation) setInterpretation(reading.interpretation);

        // Mark as already saved
        setIsSaved(true);
    }, []);

    /**
     * Reset all state
     */
    const reset = useCallback(() => {
        setPersonA({
            name: '',
            birthDate: '',
            birthTime: '',
            location: '',
            latitude: null,
            longitude: null,
            timezone: '',
            unknownTime: false
        });
        setPersonB({
            name: '',
            birthDate: '',
            birthTime: '',
            location: '',
            latitude: null,
            longitude: null,
            timezone: '',
            unknownTime: false
        });
        setLocationResults([]);
        setActivePersonForLocation(null);
        setSynastryData(null);
        setInterpretation('');
        setCurrentStep(0);
        setIsLoading(false);
        setIsSaving(false);
        setIsSaved(false);
        setError('');
    }, []);

    /**
     * Navigation helpers
     */
    const nextStep = useCallback(() => {
        setCurrentStep(prev => Math.min(prev + 1, 3));
    }, []);

    const prevStep = useCallback(() => {
        setCurrentStep(prev => Math.max(prev - 1, 0));
    }, []);

    /**
     * Validation
     */
    const isPersonValid = useCallback((person) => isPersonReady(person), []);

    const canProceed = currentStep === 0
        ? isPersonValid(personA)
        : currentStep === 1
            ? isPersonValid(personB)
            : true;

    return {
        // Person state
        personA, setPersonA,
        personB, setPersonB,

        // Location search
        locationResults,
        isSearchingLocation,
        activePersonForLocation,
        searchLocation,
        selectLocation,
        loadPersonAFromChart,

        // Synastry data
        synastryData,
        interpretation,

        // UI state
        currentStep,
        nextStep,
        prevStep,
        canProceed,
        isPersonValid,

        // Loading/saving
        isLoading,
        isLoadingInterpretation,
        isSaving,
        isSaved,
        error,
        setError,

        // Actions
        calculateSynastry,
        getInterpretation,
        saveSynastry,
        reset,

        // Saved readings
        savedReadings,
        isLoadingSavedReadings,
        loadSavedReadings,
        loadSavedReading
    };
};

export default useSynastry;
